import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { IGetGuestTokenPayload } from "../entities/DTO-models/Authentication/IGetGuestTokenPayload";
import { ISwitchTenantPayload } from "../entities/DTO-models/Authentication/ISwitchTenantPayload";
import { ISwitchTenantResponse } from "../entities/DTO-models/Authentication/ISwitchTenantResponse";
import { IPasswordLoginPayload } from "../entities/DTO-models/Authentication/IPasswordLoginPayload";
import { IPasswordLoginResponse } from "../entities/DTO-models/Authentication/IPasswordLoginResponse";
import { IPasswordRefreshPayload } from "../entities/DTO-models/Authentication/IPasswordRefreshPayload";
import { IPasswordRefreshResponse } from "../entities/DTO-models/Authentication/IPasswordRefreshResponse";
import { IPasswordChangeRequestPayload } from "../entities/DTO-models/Authentication/IPasswordChangeRequestPayload";
import { IPasswordChangeRequestResponse } from "../entities/DTO-models/Authentication/IPasswordChangeRequestResponse";
import { IPasswordSignUpPayload } from "../entities/DTO-models/Authentication/IPasswordSignUpPayload";
import { IPasswordSignUpResponse } from "../entities/DTO-models/Authentication/IPasswordSignUpResponse";

export class InValidRefreshToken extends Error {}
export class NetworkError extends Error {}
export class mtbService {
  private static _authToken: string | undefined;

  private static _APIUrl(path: string): string {
    let url = process.env.NX_PUBLIC_MTBAPI_URL;
    if (!url) {
      throw new Error("Missing required env var NX_PUBLIC_MTBAPI_URL");
    }
    let route = path;
    if (route.substring(0, 1) === "/") {
      route = route.substring(1);
    }
    if (url?.substring(url.length) === "/") {
      url = `${url}api/${route}`;
    } else {
      url = `${url}/api/${route}`;
    }
    return url;
  }

  static async postGetGuestToken(
    payload: IGetGuestTokenPayload
  ): Promise<AxiosResponse> {
    // Guest token is returned in the JSON body; cookies are not used. Omitting
    // withCredentials avoids credentialed CORS preflight (required for Capacitor
    // WebView origins such as http://localhost on native builds).
    const json: AxiosResponse = await axios.post(
      this._APIUrl("auth/getGuestToken"),
      payload,
    );
    mtbService._authToken = json.data.token;
    return json;
  }

  static async postPasswordLogin(
    payload: IPasswordLoginPayload
  ): Promise<AxiosResponse<IPasswordLoginResponse>> {
    return axios.post(this._APIUrl("auth/password/login"), payload);
  }

  static async postPasswordRefresh(
    payload: IPasswordRefreshPayload
  ): Promise<AxiosResponse<IPasswordRefreshResponse>> {
    return axios.post(this._APIUrl("auth/password/refresh"), payload);
  }

  static async postPasswordChangeRequest(
    payload: IPasswordChangeRequestPayload
  ): Promise<AxiosResponse<IPasswordChangeRequestResponse>> {
    return axios.post(this._APIUrl("auth/password/change-request"), payload);
  }

  static async postPasswordSignUp(
    payload: IPasswordSignUpPayload
  ): Promise<AxiosResponse<IPasswordSignUpResponse>> {
    return axios.post(this._APIUrl("auth/password/signup"), payload);
  }

  static async postSwitchTenant(
    payload: ISwitchTenantPayload
  ): Promise<ISwitchTenantResponse | void> {
    try {
      const config: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${mtbService._authToken}`,
        },
        withCredentials: true,
      };

      const json: AxiosResponse = await axios.post(
        this._APIUrl("auth/switchTenant"),
        payload,
        config
      );

      const data = json.data as ISwitchTenantResponse;
      return data;
    } catch (ex) {
      const axError = ex;
      if (axError.message.toLowerCase() === "network error") {
        throw new NetworkError();
      } else {
        throw ex;
      }
    }
  }

  // ... general get request wrapper
  static async getUnProtected(apiRoute: string, params?: string): Promise<any> {
    const config: AxiosRequestConfig = {
      params: params,
    };

    let json: AxiosResponse;
    try {
      json = await axios.get(this._APIUrl(apiRoute), config);
      return json;
    } catch (ex) {
      const axError = ex;
      if (axError.message.toLowerCase() === "network error") {
        throw new NetworkError();
      } else {
        throw ex;
      }
    }
  }

  // ... general get request wrapper
  static async getProtected(apiRoute: string, params?: any): Promise<any> {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${mtbService._authToken}`,
      },
      params: params,
    };

    let json: AxiosResponse;
    try {
      json = await axios.get(this._APIUrl(apiRoute), config);
      return json;
    } catch (ex) {
      const axError = ex;
      if (axError.message.toLowerCase() === "network error") {
        throw new NetworkError();
      } else {
        throw ex;
      }
    }
  }

  // ... general post request wrapper
  static async postPutFormDataProtected(
    method: "post" | "put",
    apiRoute: string,
    data: FormData,
    params?: string
  ): Promise<any> {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${mtbService._authToken}`,
        "Content-Type": "multipart/form-data",
      },
      params: params,
    };
    let json: AxiosResponse;
    try {
      if (method === "post") {
        json = await axios.post(this._APIUrl(apiRoute), data, config);
      } else {
        json = await axios.put(this._APIUrl(apiRoute), data, config);
      }
      return json;
    } catch (ex: any) {
      const axError = ex;
      if (axError.message.toLowerCase() === "network error") {
        throw new NetworkError();
      } else {
        throw ex;
      }
    }
  }

  // ... general post request wrapper
  static async postPutProtected(
    method: "post" | "put",
    apiRoute: string,
    data: string,
    params?: string
  ): Promise<any> {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${mtbService._authToken}`,
        "Content-Type": "application/json",
      },
      params: params,
    };
    let json: AxiosResponse;
    try {
      if (method === "post") {
        json = await axios.post(this._APIUrl(apiRoute), data, config);
      } else {
        json = await axios.put(this._APIUrl(apiRoute), data, config);
      }
      return json;
    } catch (ex: any) {
      const axError = ex;
      if (axError.message.toLowerCase() === "network error") {
        throw new NetworkError();
      } else {
        throw ex;
      }
    }
  }

  // ... general patch request wrapper
  static async patchProtected(
    apiRoute: string,
    data: string,
    params?: string
  ): Promise<any> {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${mtbService._authToken}`,
        "Content-Type": "application/json",
      },
      params: params,
    };
    try {
      return await axios.patch(this._APIUrl(apiRoute), data, config);
    } catch (ex: any) {
      const axError = ex;
      if (axError.message.toLowerCase() === "network error") {
        throw new NetworkError();
      } else {
        throw ex;
      }
    }
  }

  // ... general delete request wrapper
  static async deleteProtected(
    apiRoute: string,
    params?: string
  ): Promise<any> {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${mtbService._authToken}`,
        "content-type": "multipart/form-data",
      },
      params: params,
    };

    let json: AxiosResponse;
    try {
      json = await axios.delete(this._APIUrl(apiRoute), config);
      return json;
    } catch (ex) {
      const axError = ex;
      if (axError.message.toLowerCase() === "network error") {
        throw new NetworkError();
      } else {
        throw ex;
      }
    }
  }

  static setAuth0AccessToken(auth0AccesToken?: string) {
    mtbService._authToken = auth0AccesToken;
  }
}
